import { HttpService } from '@services/HttpService'
import { BEARER, APPLICATION_JSON, ACCESS_TOKEN } from '@constants'
import { config } from '@config'
import { storage } from '@services/storageService'
import i18n from '@i18n'
import { flattenObject } from '@utils/object-utils'
import { DefaultConfig } from '@constants'

export default class LocalizationService {
  private httpService = new HttpService()

  getLanguages = async () => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}core/localization/languages`
      const accessToken: string = storage.getItem(ACCESS_TOKEN)

      const result = await this.httpService.get(url, {
        headers: {
          Authorization: `${BEARER} ${accessToken}`,
          Accept: APPLICATION_JSON,
          'Content-Type': APPLICATION_JSON
        }
      })
      return result.data
    } catch (e) {
      console.log('Failed to fetch languages')
      throw new Error('settings.localization.errors.fetchLanguages')
    }
  }

  getSupportedLanguages = async () => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}core/localization/supported-languages`
      const accessToken: string = storage.getItem(ACCESS_TOKEN)

      const result = await this.httpService.get(url, {
        headers: {
          Authorization: `${BEARER} ${accessToken}`,
          Accept: APPLICATION_JSON,
          'Content-Type': APPLICATION_JSON
        }
      })
      return result.data
    } catch (e) {
      console.log('Failed to fetch supported languages')
      throw new Error('settings.localization.errors.fetchSupportedLanguages')
    }
  }

  addSupportedLanguage = async code => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}core/localization/supported-languages/${code}`
      const accessToken: string = storage.getItem(ACCESS_TOKEN)

      const result = await this.httpService.put(
        url,
        {},
        {
          headers: {
            Authorization: `${BEARER} ${accessToken}`,
            Accept: APPLICATION_JSON,
            'Content-Type': APPLICATION_JSON
          }
        }
      )
      return result.data
    } catch (e) {
      console.log('Failed to add supported language')
      throw new Error('settings.localization.errors.addSupportedLanguage')
    }
  }

  deleteSupportedLanguage = async code => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}core/localization/supported-languages/${code}`
      const accessToken: string = storage.getItem(ACCESS_TOKEN)

      const result = await this.httpService.delete(url, {
        headers: {
          Authorization: `${BEARER} ${accessToken}`,
          Accept: APPLICATION_JSON,
          'Content-Type': APPLICATION_JSON
        }
      })
      return result.data
    } catch (e) {
      console.log('Failed to remove a supported language')
      throw new Error('settings.localization.errors.deleteSupportedLanguage')
    }
  }

  getLanguageBundle = async () => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}core/localization/content`
      const accessToken: string = storage.getItem(ACCESS_TOKEN)

      const result = await this.httpService.get(url, {
        headers: {
          Authorization: `${BEARER} ${accessToken}`,
          Accept: APPLICATION_JSON,
          'Content-Type': APPLICATION_JSON
        }
      })
      return result.data
    } catch (e) {
      console.log('Failed to fetch language bundle')
      throw new Error('settings.localization.errors.fetchLanguageBundle')
    }
  }

  updateLanguageBundle = async updatedBundle => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}core/localization/content`
      const accessToken: string = storage.getItem(ACCESS_TOKEN)

      const result = await this.httpService.put(url, updatedBundle, {
        headers: {
          Authorization: `${BEARER} ${accessToken}`,
          Accept: APPLICATION_JSON,
          'Content-Type': APPLICATION_JSON
        }
      })
      return result.data
    } catch (e) {
      console.log('Failed to update language bundle')
      throw new Error('settings.localization.errors.updateLanguageBundle')
    }
  }

  getLanguageTranslations = (languageCode: string) => {
    const serverTranslations = i18n.getResourceBundle(
      languageCode,
      config.localization.defaultNamespace || DefaultConfig.LOCALIZATION_DEFAULT_NAMESPACE
    )
    const localTranslations = i18n.getResourceBundle(
      languageCode,
      config.localization.fallbackNamespace ||
        DefaultConfig.LOCALIZATION_FALLBACK_NAMESPACE
    )
    const flattenedServerTranslation = flattenObject(serverTranslations)
    const flattenedLocalTranslation = flattenObject(localTranslations)
    const mergedTranslations = {
      ...flattenedLocalTranslation,
      ...flattenedServerTranslation
    }
    return mergedTranslations
  }
}
